/**
 * «Ask about the deal» next to the record's CRM link (#765 Ф2, spec §8.0;
 * subject model R1): the entry point that CHOOSES the deal level. The panel
 * then answers from every conversation of that deal, lead, contact or
 * company, and the topic follows the deal of whatever record is on screen.
 *
 * Renders nothing unless the assistant is available here (the drawer context
 * decides that once) AND the entity contour is released: until its legal
 * texts ship, the server answers `scope_not_available`, and a button that
 * can only fail is worse than none. The same for a subject whose access may
 * not ask about a record or its deal — an analyst's grant (#1677): the
 * provider projects no deal for it (`screenEntityTopic` is `null`).
 */

import { Sparkles } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { isAssistantScopeReleased } from '@aica/api-types/schemas';
import { useAssistantDrawerSafe } from '@/contexts/AssistantDrawerContext';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';

export interface AssistantEntityChipProps {
  className?: string;
}

/**
 * The deal comes from the drawer's `screenEntityTopic` — the one construction
 * of the entity topic, from the record on screen — not rebuilt here: a
 * second copy could open a subject that following would at once replace
 * (#1041 review; the sidebar button does the same for the record, #922).
 */
export function AssistantEntityChip({ className }: AssistantEntityChipProps) {
  const { t } = useTranslation('assistant');
  const drawer = useAssistantDrawerSafe();
  const entityTopic = drawer?.screenEntityTopic ?? null;
  if (!drawer || !entityTopic || !isAssistantScopeReleased('entity')) return null;

  return (
    // The shared control and its `sm` size, like the series button beside it
    // on the same header line: the size comes from the variant, never from
    // the call site (Codex P2, #1041).
    <Button
      variant="ghost"
      size="sm"
      onClick={() => drawer.openAssistant(entityTopic)}
      className={cn(
        'inline-flex items-center gap-1 text-xs text-ai hover:text-ai-hover',
        className
      )}
      data-testid="assistant-entity-chip"
    >
      <Sparkles className="h-3.5 w-3.5" aria-hidden="true" />
      {t(`askAbout.${entityTopic.subject.entityType}`)}
    </Button>
  );
}
